import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import { DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { LoadTotals } from 'src/components/dispatch/vehicleLoad';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { DispatchCard } from 'src/entities/dispatch';

import { LegRunQuantityMap, OperationPlace } from './buildOperationPlaces';

type ExpandedStopBase = {
  /** React key。地点ごとに一意（構成 operation の id 由来） */
  stopId: string;
  /** この地点を構成する operation。並べ替えの保存で送る id 列に使う */
  operationIds: number[];
  /** 詳細選択で集約カードを解決するのに使う。再訪は同じ値になりうる */
  cardIndex: number;
  /** 既存 DispatchSelection へ渡す集約カード順。表示番号ではない */
  dispatchOrderIndex: number;
  /**
   * 表示する地点番号。truck 内で回転をまたいだ通し番号で、地図マーカーと同じ値。
   * /plans の PlanningsOperationDelivery:89-95 の seriesIndex と同じ考え方。
   */
  seriesIndex: number;
  locationName: string;
  /** 地図マーカーと共用する座標。引けなければ null */
  position: [number, number] | null;
  /** 到着時刻 HH:mm */
  time: string;
  workMinutes: number;
  waitMinutes: number;
  /** この地点で扱う量。構成 legRun の合算 */
  metricText: string;
  /**
   * この訪問で扱う量。metricText と違い構造化した値。
   * 共通配送先が行ごとの数量と合計を出すのに使う（表示は metricText のまま）
   */
  metrics: {
    itemCount: number;
    weightKg: number;
    volumeM3: number;
    /** この訪問に含まれる distinct shipment_order の数 */
    invoiceCount: number;
  };
  /**
   * この訪問に含まれる案件 ID。新規割当ハイライトの判定に使う。
   * invoiceCount と違い「どの案件か」まで持つ（件数だけでは差分と突き合わせられない）
   */
  shipmentOrderIds: number[];
  /**
   * この地点の指定時間の超過 / バース予約の時間外。判定はカード生成時
   * (adapter.ts の isOutsideSpecifiedTime / isOutsideBerthReservation) に済んでいるので、
   * 対応するカードから拾う。V3Plans も地点カードに警告アイコンを出している
   * (PlanningsPlace.presenter.tsx:293-302)。
   */
  hasAlert: boolean;
  hasBerthAlert: boolean;
};

export type ExpandedStopVM =
  | (ExpandedStopBase & { kind: 'loading' })
  | (ExpandedStopBase & { kind: 'unloading' });

export type ExpandedRowVM = {
  /** 計画開始・終了。算出不能なら null */
  planStartAt: Date | null;
  planEndAt: Date | null;
  /** ヘッダー右の容量表示 */
  capacityText: string;
  /** 出発の行。最初の実配送行だけに出す */
  departureTerminal: TerminalVM | null;
  /**
   * 帰着の行。最後の実配送行だけに出す。
   * 1 つの terminal を先頭と末尾の両方に描いていたため、2 回転ある勤務で
   * 「出発・積・降・帰着・積・降」と 1 回転目の途中で帰庫したように見えていた。
   */
  arrivalTerminal: TerminalVM | null;
  /** 運行順（arrivalAt 昇順） */
  stops: ExpandedStopVM[];
};

const hhmm = (date: Date | null): string => (date ? datetimeDecorator.toHourMinutes(date) ?? '' : '');

const toDateOrNull = (iso: string | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** LaneCard.tsx:48-51 と同じ単位・書式。区切りは同ファイルの join(' / ') に合わせる */
function formatMetrics(
  values: { itemCount: number; weightKg: number; volumeM3: number },
  displayMetrics: ReadonlySet<DisplayMetric>,
): string {
  const parts: string[] = [];
  if (displayMetrics.has('quantity')) parts.push(`${quantityValueText(values.itemCount)} cs`);
  if (displayMetrics.has('weight')) parts.push(`${weightValueText(values.weightKg)}kg`);
  if (displayMetrics.has('volume')) parts.push(`${volumeValueText(values.volumeM3)}m³`);
  return parts.join(' / ');
}

/**
 * 展開行の表示内容を算出する。計算はすべてここに集める（サンプルは 130 行超を JSX 内の
 * 即時関数に書いており jest で検証できない。P2c の buildGrossProfitByTruckId と同じ方針）。
 *
 * 時刻・作業時間・待機時間は API の実測値をそのまま使う。arrivalAt / departureAt が
 * ISO 日時なので日跨ぎの推測も既定値の補完も不要。
 */
/** 出発地・帰着地の 1 行 */
export type TerminalVM = { locationName: string; time: string | null; address?: string };

/** delivery の departure_operation / arrival_operation から引いた出発・帰着の実績 */
export type TerminalSource = { name: string; address?: string; at?: string };

export function buildExpandedRowVM(params: {
  /** arrivalAt 昇順に集約済み。この関数では並べ替えない（Task 1 の buildOperationPlaces が済ませている） */
  places: readonly OperationPlace[];
  quantities: LegRunQuantityMap;
  displayMetrics: ReadonlySet<DisplayMetric>;
  /**
   * その行の集約カード（生の配列）。指定時間・バース予約の警告を地点へ移すのに使う。
   */
  cards: readonly DispatchCard[];
  /** この回転の積載量。勤務全体を走査する buildPeakLoadByRowId が呼び出し側で作る */
  peakLoad: LoadTotals;
  /** その車両で最初に実配送がある行か。計画開始と出発の行の判定に使う */
  isFirstActiveRow: boolean;
  /** その車両で最後に実配送がある行か。帰着の行をここに出す */
  isLastActiveRow?: boolean;
  /** この行より前（同じ truck の前の回転）までに振った地点数。番号はこの続きから 1 始まりで振る */
  seriesIndexBase: number;
  /**
   * 勤務開始の完全な ISO 日時（delivery.startAt 由来）。isFirstActiveRow のとき計画開始に優先使用。
   * HH:mm にすると「勤務開始 23:00、先頭 operation が翌 01:00」で同日 23 時か前日 23 時か判別できない。
   */
  planStartAt?: string;
  /** 出発・帰着の行に使う車庫（名前と住所）。operation が無いときの代用。引けないなら undefined */
  garage?: { name: string; address?: string };
  /** delivery.departure_operation。実際の出庫地点と時刻 */
  departure?: TerminalSource;
  /** delivery.arrival_operation。実際の帰着地点と時刻 */
  arrival?: TerminalSource;
}): ExpandedRowVM {
  const {
    places, quantities, displayMetrics, cards, peakLoad, isFirstActiveRow, isLastActiveRow = false,
    seriesIndexBase, planStartAt, garage, departure, arrival,
  } = params;

  const stopVMs: ExpandedStopVM[] = places.map((place, index) => {
    // 地点に複数 legRun がぶら下がる（1 停車で複数件積む/降ろす）ので合算する
    const invoiceIds = new Set<number>();
    const total = place.shipmentOrderLegRunIds.reduce(
      (acc, id) => {
        const quantity = quantities.get(id);
        if (quantity) {
          acc.itemCount += quantity.itemCount;
          acc.weightKg += quantity.weightKg;
          acc.volumeM3 += quantity.volumeM3;
          if (quantity.shipmentOrderId != null) invoiceIds.add(quantity.shipmentOrderId);
        }
        return acc;
      },
      { itemCount: 0, weightKg: 0, volumeM3: 0 },
    );
    const card = cards[place.cardIndex];
    return {
      stopId: place.placeId,
      operationIds: place.operationIds,
      cardIndex: place.cardIndex,
      dispatchOrderIndex: place.dispatchOrderIndex,
      seriesIndex: seriesIndexBase + index + 1,
      locationName: place.locationName,
      position: place.position,
      time: hhmm(toDateOrNull(place.arrivalAt)),
      workMinutes: Math.round(place.workSeconds / 60),
      waitMinutes: Math.round(place.waitSeconds / 60),
      metricText: formatMetrics(total, displayMetrics),
      metrics: { ...total, invoiceCount: invoiceIds.size },
      shipmentOrderIds: [...invoiceIds],
      kind: place.kind,
      hasAlert: card?.hasAlert ?? false,
      hasBerthAlert: card?.hasBerthAlert ?? false,
    };
  });

  const firstArrival = toDateOrNull(places[0]?.arrivalAt);
  const planStartAtDate = isFirstActiveRow
    ? toDateOrNull(planStartAt) ?? firstArrival
    : firstArrival;

  // 容量は車両カードのバーと同じ「同時に積んでいる量の最大」。降ろす量を単純合計すると
  // 途中で降ろして積み直す日にバーと違う（過大な）値が出る (log_web_issues#309)
  const capacity = { itemCount: peakLoad.cases, weightKg: peakLoad.kg, volumeM3: peakLoad.m3 };

  const planEndAt = toDateOrNull(places[places.length - 1]?.departureAt);

  // operation があれば実績の地点・時刻を使う。無い勤務 (配車前など) は車庫と計画時刻で代用し、
  // 車庫も引けなければ行ごと出さない（仮の値で埋めない）
  const terminalOf = (source: TerminalSource | undefined, fallbackTime: Date | null): TerminalVM | null => {
    if (source) return { locationName: source.name, time: hhmm(toDateOrNull(source.at)) || null, address: source.address };
    if (!garage) return null;
    return { locationName: garage.name, time: hhmm(fallbackTime) || null, address: garage.address };
  };

  return {
    planStartAt: planStartAtDate,
    planEndAt,
    capacityText: formatMetrics(capacity, displayMetrics),
    departureTerminal: isFirstActiveRow ? terminalOf(departure, planStartAtDate) : null,
    arrivalTerminal: isLastActiveRow ? terminalOf(arrival, planEndAt) : null,
    stops: stopVMs,
  };
}
